import type { AssignableRole } from "@unbox/api";
import { Check } from "@untitledui/icons";
import { Text as AriaText } from "react-aria-components";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

/** Human labels for the scoped, customer-facing role taxonomy (UNB-1912). */
export const AssignableRoleLabels: Record<AssignableRole, string> = {
  user: "User",
  merchant_admin: "Merchant Admin",
  workspace_admin: "Workspace Admin",
};

export const AssignableRoleDescriptions: Record<AssignableRole, string> = {
  user: "Full use of the platform, with view-only access to safety risks and policies",
  merchant_admin: "Everything a User can do, plus configure safety risks and policies",
  workspace_admin: "Everything a Merchant Admin can do, across the whole workspace",
};

/** A concrete scoped role, or the derived, display-only "multiple" state (UNB-2001). */
export type ScopedRoleValue = AssignableRole | "multiple";

export const scopedRoleLabels: Record<ScopedRoleValue, string> = {
  ...AssignableRoleLabels,
  multiple: "Multiple roles",
};

export const scopedRoleDescriptions: Record<ScopedRoleValue, string> = {
  ...AssignableRoleDescriptions,
  multiple: "Different roles across the merchants in this workspace",
};

export const RoleOption = ({
  role,
  isSelected,
  isDisabled,
}: {
  role: ScopedRoleValue;
  isSelected: boolean;
  isDisabled: boolean;
}) => (
  <>
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <AriaText slot="label" className={cx("text-sm font-medium", isDisabled ? "text-disabled" : "text-primary")}>
        {scopedRoleLabels[role]}
      </AriaText>
      <AriaText slot="description" className={cx("text-xs", isDisabled ? "text-disabled" : "text-tertiary")}>
        {scopedRoleDescriptions[role]}
      </AriaText>
    </span>
    {isSelected && <Check aria-hidden="true" className="size-4 shrink-0 text-fg-brand-primary" />}
  </>
);

// Single source of truth for the scoped-role colours (UNB-2001, Figma). Shared by
// the RoleSelect dropdown pill and the read-only role badges in the user tables.
// `badge` = border/background/text; `chevron` = the dropdown caret colour.
export const rolePillStyles: Record<ScopedRoleValue, { badge: string; chevron: string }> = {
  workspace_admin: { badge: "border-[#1849A9] bg-[#102A56] text-[#84CAFF]", chevron: "text-[#2E90FA]" },
  merchant_admin: { badge: "border-[#9E165F] bg-[#4E0D30] text-[#FAA7E0]", chevron: "text-[#EE46BC]" },
  user: { badge: "border-[#53389E] bg-[#2C1C5F] text-[#D6BBFB]", chevron: "text-[#B692F6]" },
  // Derived state — a user whose merchants hold different roles within a workspace.
  multiple: { badge: "border-[#373A41] bg-[#13161B] text-[#CECFD2]", chevron: "text-[#CECFD2]" },
};

/** Read-only pill badge for a scoped role — matches the RoleSelect dropdown pill. */
export const ScopedRoleBadge = ({ role }: { role: ScopedRoleValue }) => (
  <Tooltip title={scopedRoleDescriptions[role]}>
    <TooltipTrigger className="inline-flex cursor-default">
      <span
        className={cx(
          "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
          rolePillStyles[role].badge,
        )}
      >
        {scopedRoleLabels[role]}
      </span>
    </TooltipTrigger>
  </Tooltip>
);

/**
 * A user's role badge: the scoped role as a pill (User / Merchant Admin /
 * Workspace Admin / Multiple roles). Internal staff are shown by the scoped role
 * they hold too — console access is a separate axis and "Admin" in a role column
 * reads as a fourth scoped role (UNB-2129). Pass `scopedRole` for a
 * workspace-scoped value; otherwise the role across all memberships is shown.
 */
export const EffectiveRoleLabel = ({
  effectiveRole,
  scopedRole,
}: {
  effectiveRole?: ScopedRoleValue;
  scopedRole?: ScopedRoleValue;
}) => <ScopedRoleBadge role={scopedRole ?? effectiveRole ?? "user"} />;

/** An invite's role badge: the scoped role the invite grants. */
export const InviteRoleLabel = ({ assignableRole }: { assignableRole: AssignableRole }) => (
  <ScopedRoleBadge role={assignableRole} />
);
